import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Backdrop,
  IconButton,
  Tooltip,
  TableSortLabel
} from '@mui/material';
import { apiClient } from '../../api/apiClient';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';
import { debounce } from '@mui/material/utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Instanz-Definitionen
const INSTANZ_COLORS = {
  1: '#4caf50', // Grün für erste Instanz (SG)
  2: '#2196f3', // Blau für zweite Instanz (LSG)
  3: '#9c27b0'  // Violett für dritte Instanz (BSG)
};

const INSTANZ_LABELS = {
  1: 'I',
  2: 'II',
  3: 'III'
};

export default function EntscheidungenList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [entscheidungen, setEntscheidungen] = useState([]);
  const [gerichte, setGerichte] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sachgebiete, setSachgebiete] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter-States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedGericht, setSelectedGericht] = useState('');
  const [selectedSachgebiet, setSelectedSachgebiet] = useState('');
  const [selectedKategorie, setSelectedKategorie] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [aktenzeichenFilter, setAktenzeichenFilter] = useState('');
  const [volltextFilter, setVolltextFilter] = useState('');
  const [userPermissions, setUserPermissions] = useState([]);

  // Sortier-States
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Synchronisiere States mit URL-Parametern
  useEffect(() => {
    const pageParam = searchParams.get('page');
    const sizeParam = searchParams.get('size');
    const gerichtParam = searchParams.get('gericht');
    const sachgebietParam = searchParams.get('sachgebiet');
    const kategorieParam = searchParams.get('kategorie');
    const userParam = searchParams.get('user');
    const aktenzeichenParam = searchParams.get('aktenzeichen');
    const volltextParam = searchParams.get('volltext');
    const sortByParam = searchParams.get('sort_by');
    const sortOrderParam = searchParams.get('sort_order');

    if (pageParam !== null) setPage(parseInt(pageParam));
    if (sizeParam !== null) setRowsPerPage(parseInt(sizeParam));
    if (gerichtParam !== null) setSelectedGericht(gerichtParam);
    if (sachgebietParam !== null) setSelectedSachgebiet(sachgebietParam);
    if (kategorieParam !== null) setSelectedKategorie(kategorieParam);
    if (userParam !== null) setSelectedUser(userParam);
    if (aktenzeichenParam !== null) setAktenzeichenFilter(aktenzeichenParam);
    if (volltextParam !== null) setVolltextFilter(volltextParam);
    if (sortByParam !== null) setSortBy(sortByParam);
    if (sortOrderParam !== null) setSortOrder(sortOrderParam);
  }, [searchParams]);

  // Update URL wenn sich Filter ändern
  const updateURL = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const handleGerichtChange = (event) => {
    const value = event.target.value;
    setSelectedGericht(value);
    updateURL({ gericht: value });
  };

  const handleSachgebietChange = (event) => {
    const value = event.target.value;
    setSelectedSachgebiet(value);
    updateURL({ sachgebiet: value });
  };

  const handleKategorieChange = (event) => {
    const value = event.target.value;
    setSelectedKategorie(value);
    updateURL({ kategorie: value });
  };

  const handleUserChange = (event) => {
    const value = event.target.value;
    setSelectedUser(value);
    updateURL({ user: value });
  };

  const handleAktenzeichenFilterChange = (event) => {
    const value = event.target.value;
    setAktenzeichenFilter(value);
    updateURL({ aktenzeichen: value });
  };

  const handleVolltextFilterChange = (event) => {
    const value = event.target.value;
    setVolltextFilter(value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    updateURL({ page: newPage.toString() });
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    updateURL({ size: newSize.toString(), page: '0' });
  };

  // Sortierhandling
  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
    updateURL({ sort_by: field, sort_order: newOrder });
  };

  // Initialisierung
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        // Hole zuerst die Benutzerinformationen und Berechtigungen
        const meResponse = await apiClient.get('/auth/me', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // Extrahiere die Permissions aus der Antwort
        const permissions = meResponse.data.role?.permissions?.map(p => p.name) || [];
        setUserPermissions(permissions);

        // Prüfe ob der Benutzer Admin oder Landmaster ist
        const isAdmin = permissions.includes('is_admin');
        const isLandmaster = permissions.includes('is_landmaster');

        // Hole die Entscheidungen
        const entscheidungenResponse = await apiClient.get('/entscheidungen/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // Hole die Benutzer nur wenn Admin oder Landmaster
        if (isAdmin || isLandmaster) {
          try {
            const usersResponse = await apiClient.get(`/users/${isLandmaster ? '?assigned=true' : ''}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            setUsers(usersResponse.data);
          } catch (error) {
            console.error('Fehler beim Laden der Benutzer:', error);
            // Fehler beim Laden der Benutzer sollte nicht die gesamte Initialisierung blockieren
          }
        }
        
        if (entscheidungenResponse.data.items?.[0]) {
          const firstItem = entscheidungenResponse.data.items[0];
          if (firstItem.available_sachgebiete) {
            setSachgebiete(firstItem.available_sachgebiete);
          }
          if (firstItem.available_kategorien) {
            setKategorien(firstItem.available_kategorien);
          }
        }

        try {
          const gerichteResponse = await apiClient.get('/gerichte/', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          setGerichte(gerichteResponse.data);
        } catch (error) {
          console.error('Fehler beim Laden der Gerichte:', error);
        }
      } catch (error) {
        console.error('Fehler bei der Initialisierung:', error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Lade Entscheidungen
  const fetchEntscheidungen = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        size: rowsPerPage.toString(),
        editable: 'true'
      });

      if (aktenzeichenFilter) {
        params.append('aktenzeichen', aktenzeichenFilter);
      }

      if (selectedGericht) {
        params.append('gericht_id', selectedGericht);
      }

      if (selectedSachgebiet) {
        params.append('sachgebiet', selectedSachgebiet);
      }

      if (selectedKategorie) {
        params.append('kategorie', selectedKategorie);
      }

      if (selectedUser) {
        params.append('last_change_user_id', selectedUser);
      }

      if (volltextFilter) {
        params.append('volltext', volltextFilter);
      }

      if (sortBy && sortOrder) {
        params.append('sort_by', sortBy);
        params.append('sort_order', sortOrder);
      }

      const response = await apiClient.get(`/entscheidungen/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setEntscheidungen(response.data.items);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error('Fehler beim Laden der Entscheidungen:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, aktenzeichenFilter, selectedGericht, selectedSachgebiet, selectedKategorie, selectedUser, volltextFilter, sortBy, sortOrder]);

  // Debounced Volltext-Suche
  const debouncedFetchEntscheidungen = useCallback(() => {
    // Aktualisiere URL-Parameter nur wenn Volltextsuche aktiv
    if (volltextFilter) {
      updateURL({ volltext: volltextFilter });
    } else {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('volltext');
      setSearchParams(newParams);
    }
    fetchEntscheidungen();
  }, [volltextFilter, updateURL, searchParams, setSearchParams, fetchEntscheidungen]);

  // Erstelle eine debounced Version der Funktion
  const debouncedFetchEntscheidungenWrapper = useMemo(
    () => debounce(debouncedFetchEntscheidungen, 500),
    [debouncedFetchEntscheidungen]
  );

  // Lade Entscheidungen wenn sich Filter ändern
  useEffect(() => {
    debouncedFetchEntscheidungenWrapper();
    // Cleanup function
    return () => {
      debouncedFetchEntscheidungenWrapper.clear();
    };
  }, [debouncedFetchEntscheidungenWrapper]);

  const handleEdit = (event, entscheidung) => {
    // Wenn es ein mittlerer Mausklick war, lassen wir das Standard-Verhalten zu
    if (event.button !== 1) {
      event.preventDefault();
      // Aktuelle URL mit Suchparametern speichern
      const currentPath = `${location.pathname}${location.search}`;
      navigate(`/admin/entscheidungen/edit/${entscheidung.id}?returnTo=${encodeURIComponent(currentPath)}`);
    }
  };

  const handleAddNew = () => {
    navigate('/admin/entscheidungen/add');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Entscheidung wirklich löschen?')) {
      try {
        await apiClient.delete(`/entscheidungen/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        fetchEntscheidungen();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  // Korrigiere die getInstanzFromGericht Funktion mit Debug-Logging
  const getInstanzFromGericht = (entscheidung) => {
    if (!entscheidung || !gerichte) return null;
    
    // Ermittle die Gerichts-ID entweder direkt oder aus dem gericht-Objekt
    const gerichtId = entscheidung.gericht_id || (entscheidung.gericht && entscheidung.gericht.id);
    if (!gerichtId) return null;
    
    // Hole das Gericht aus der gerichte-Liste anhand der ID
    const gerichtDetails = gerichte.find(g => g.id === gerichtId);
    if (!gerichtDetails) return null;

    // Bestimme die Instanz basierend auf der Bezeichnung
    if (gerichtDetails.bezeichnung.includes('Bundessozialgericht')) {
      return 3;
    } else if (gerichtDetails.bezeichnung.includes('Landessozialgericht')) {
      return 2;
    } else if (gerichtDetails.bezeichnung.includes('Sozialgericht')) {
      return 1;
    }
    return null;
  };

  // Wähle das korrekte Datum basierend auf der Instanz
  const getInstanzDatum = (entscheidung) => {
    const instanz = getInstanzFromGericht(entscheidung);

    // Verwende nur das zur Instanz passende Datum
    switch(instanz) {
      case 1:
        return entscheidung.datum_1_instanz;
      case 2:
        return entscheidung.datum_2_instanz;
      case 3:
        return entscheidung.datum_3_instanz;
      default:
        // Wenn keine Instanz erkannt wurde, nehmen wir das erste verfügbare Datum
        return entscheidung.datum_1_instanz || entscheidung.datum_2_instanz || entscheidung.datum_3_instanz;
    }
  };

  // Wähle das korrekte Aktenzeichen basierend auf der Instanz
  const getInstanzAktenzeichen = (entscheidung) => {
    const instanz = getInstanzFromGericht(entscheidung);
    switch(instanz) {
      case 1:
        return entscheidung.aktenzeichen || entscheidung.aktenzeichen_1_instanz;
      case 2:
        return entscheidung.aktenzeichen_2_instanz;
      case 3:
        return entscheidung.aktenzeichen_3_instanz;
      default:
        return entscheidung.aktenzeichen || entscheidung.aktenzeichen_1_instanz || entscheidung.aktenzeichen_2_instanz || entscheidung.aktenzeichen_3_instanz;
    }
  };

  // Instanz-Text für Tooltip
  const getInstanzText = (instanz, gerichtBezeichnung) => {
    switch(instanz) {
      case 1:
        return `Erstinstanzliche Entscheidung des ${gerichtBezeichnung}`;
      case 2:
        return `Zweitinstanzliche Entscheidung des ${gerichtBezeichnung}`;
      case 3:
        return `Letztinstanzliche Entscheidung des ${gerichtBezeichnung}`;
      default:
        return '';
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Entscheidungen verwalten
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Gericht</InputLabel>
          <Select
            value={selectedGericht}
            onChange={handleGerichtChange}
            label="Gericht"
          >
            <MenuItem value="">Alle</MenuItem>
            {gerichte.map((gericht) => (
              <MenuItem key={gericht.id} value={gericht.id}>
                {gericht.bezeichnung}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Sachgebiet</InputLabel>
          <Select
            value={selectedSachgebiet}
            onChange={handleSachgebietChange}
            label="Sachgebiet"
          >
            <MenuItem value="">Alle</MenuItem>
            {sachgebiete.map((sg) => (
              <MenuItem key={sg.code} value={sg.code}>
                {sg.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Kategorie</InputLabel>
          <Select
            value={selectedKategorie}
            onChange={handleKategorieChange}
            label="Kategorie"
          >
            <MenuItem value="">Alle</MenuItem>
            {kategorien.map((kat) => (
              <MenuItem key={kat.code} value={kat.code}>
                {kat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(userPermissions.includes('is_admin') || userPermissions.includes('is_landmaster')) && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Bearbeiter</InputLabel>
            <Select
              value={selectedUser}
              onChange={handleUserChange}
              label="Bearbeiter"
            >
              <MenuItem value="">Alle</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="Aktenzeichen"
          value={aktenzeichenFilter}
          onChange={handleAktenzeichenFilterChange}
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="Volltext"
          value={volltextFilter}
          onChange={handleVolltextFilterChange}
          sx={{ minWidth: 200 }}
          placeholder='Suchbegriffe mit OR oder in "Anführungszeichen"'
          helperText="Mehrere Begriffe werden mit UND verknüpft"
        />

        <Button
          variant="contained"
          onClick={handleAddNew}
          sx={{ height: 56 }}
        >
          Neue Entscheidung
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  padding: '6px 6px 6px 16px',
                  width: '48px'
                }}
              >
                <TableSortLabel
                  active={sortBy === 'active'}
                  direction={sortBy === 'active' ? sortOrder : 'asc'}
                  onClick={() => handleSort('active')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell 
                sx={{ 
                  padding: '6px 6px 6px 16px',
                  width: '48px'
                }}
              >
                <TableSortLabel
                  active={sortBy === 'instanz'}
                  direction={sortBy === 'instanz' ? sortOrder : 'asc'}
                  onClick={() => handleSort('instanz')}
                >
                  Instanz
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Aktenzeichen</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Gericht</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <TableSortLabel
                  active={sortBy === 'sachgebiet'}
                  direction={sortBy === 'sachgebiet' ? sortOrder : 'asc'}
                  onClick={() => handleSort('sachgebiet')}
                >
                  Sachgebiet
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <TableSortLabel
                  active={sortBy === 'kategorie'}
                  direction={sortBy === 'kategorie' ? sortOrder : 'asc'}
                  onClick={() => handleSort('kategorie')}
                >
                  Kategorie
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Datum</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <TableSortLabel
                  active={sortBy === 'last_change_user'}
                  direction={sortBy === 'last_change_user' ? sortOrder : 'asc'}
                  onClick={() => handleSort('last_change_user')}
                >
                  Bearbeiter
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <TableSortLabel
                  active={sortBy === 'last_change_date'}
                  direction={sortBy === 'last_change_date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('last_change_date')}
                >
                  Letzte Änderung
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entscheidungen.map((entscheidung) => (
              <TableRow 
                key={entscheidung.id}
                component={Link}
                to={`/admin/entscheidungen/edit/${entscheidung.id}`}
                onClick={(e) => handleEdit(e, entscheidung)}
                sx={{ 
                  '&:hover': { 
                    backgroundColor: 'action.hover',
                    cursor: 'pointer'
                  },
                  textDecoration: 'none',
                  color: 'inherit',
                  ...(entscheidung.active === false && {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  })
                }}
              >
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    width: '48px'
                  }}
                >
                  <Tooltip title={entscheidung.active ? "Aktiv" : "Deaktiviert"}>
                    {entscheidung.active ? (
                      <CheckCircleIcon 
                        sx={{ 
                          color: 'success.main',
                          fontSize: '20px'
                        }} 
                      />
                    ) : (
                      <CancelIcon 
                        sx={{ 
                          color: 'error.main',
                          fontSize: '20px'
                        }} 
                      />
                    )}
                  </Tooltip>
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    width: '48px'
                  }}
                >
                  {(() => {
                    const instanz = getInstanzFromGericht(entscheidung);
                    if (!instanz) return null;
                    
                    return (
                      <Tooltip 
                        title={getInstanzText(instanz, entscheidung.gericht?.bezeichnung)}
                        arrow
                      >
                        <div style={{ display: 'inline-block' }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: INSTANZ_COLORS[instanz],
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {INSTANZ_LABELS[instanz]}
                          </Box>
                        </div>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    ...(entscheidung.active === false && {
                      color: 'text.disabled'
                    })
                  }}
                >
                  {getInstanzAktenzeichen(entscheidung)}
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    ...(entscheidung.active === false && {
                      color: 'text.disabled'
                    })
                  }}
                >
                  {entscheidung.gericht?.bezeichnung || '-'}
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    ...(entscheidung.active === false && {
                      color: 'text.disabled'
                    })
                  }}
                >
                  {sachgebiete.find(sg => sg.code === entscheidung.sachgebiet)?.name || entscheidung.sachgebiet}
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    ...(entscheidung.active === false && {
                      color: 'text.disabled'
                    })
                  }}
                >
                  {kategorien.find(k => k.code === entscheidung.kategorie)?.name || entscheidung.kategorie}
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    ...(entscheidung.active === false && {
                      color: 'text.disabled'
                    })
                  }}
                >
                  {(() => {
                    const datum = getInstanzDatum(entscheidung);
                    return datum ? format(new Date(datum), 'dd.MM.yyyy', { locale: de }) : '-';
                  })()}
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    ...(entscheidung.active === false && {
                      color: 'text.disabled'
                    })
                  }}
                >
                  {entscheidung.last_change_user?.username || '-'}
                </TableCell>
                <TableCell 
                  sx={{ 
                    padding: '6px 6px 6px 16px',
                    ...(entscheidung.active === false && {
                      color: 'text.disabled'
                    })
                  }}
                >
                  {entscheidung.last_change_timestamp
                    ? format(new Date(entscheidung.last_change_timestamp), 'dd.MM.yyyy HH:mm', { locale: de })
                    : '-'}
                </TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px' }} onClick={(e) => e.stopPropagation()}>
                  <Button onClick={(e) => handleEdit(e, entscheidung)} size="small">
                    Bearbeiten
                  </Button>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(entscheidung.id);
                    }} 
                    size="small" 
                    color="error"
                    aria-label="Löschen"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Zeilen pro Seite:"
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
} 